import ExpandMoreIcon from '@mui/icons-material/NavigateNext';
import { Checkbox } from '@mui/material';
import AccordionDetails from '@mui/material/AccordionDetails/AccordionDetails';
import useTheme from '@mui/material/styles/useTheme';
import { GridRowId } from '@mui/x-data-grid-premium';
import { CXAvatar } from 'common/components/cx-avatar/cx-avatar';
import { EllipsisText } from 'common/components/ellipsis-text/ellipsis-text.component';
import Accordion, { StyledAccordionSummary } from 'common/components/material/Accordion';
import Box from 'common/components/material/Box';
import Card from 'common/components/material/Card';
import { DrawerHeader } from 'common/components/material/Drawer';
import Skeleton from 'common/components/material/Skeleton';
import Stack from 'common/components/material/Stack';
import Tooltip from 'common/components/material/Tooltip';
import Typography from 'common/components/material/Typography';
import { ButtonTabs, CloseButton } from 'common/components/navigation';
import { ExploreTabEnum } from 'common/enum/ExploreTab.enum';
import useEventEmitter from 'common/hooks/eventHooks/useEventEmitter';
import useDrawer from 'common/hooks/useDrawer';
import useInfiniteQuery from 'common/hooks/useInfiniteQuery';
import useQuery from 'common/hooks/useQuery';
import useSession from 'common/hooks/useSession';
import useView from 'common/hooks/useView';
import InfiniteScrollContainer from 'features/org-root/components/InfiniteScrollContainer';
import useIntersectionObserver from 'features/org-root/hooks/useIntersectionObserver';
import {
  createCursorQueryParams,
  getNextCursorParam,
} from 'features/org-root/utils/infiniteScrollApi.utils';
import EndorsedSkillTrackRecord, {
  RecentlyWorkedItem,
} from 'features/profiles/components/EdnorsedSkillTrackRecord';
import { UserAvatar } from 'features/profiles/components/user-avatar';
import SearchSkeletonView from 'features/search/components/search-skeleton-view.component';
import { mapSearchParamsObject } from 'features/search/search.utils';
import { ActiveFilters, SearchFilters } from 'features/search/types/filters.type';
import { SearchParams } from 'features/search/types/search-params.type';
import { partition } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  COMPARISON_VIEW_PARAM,
  VIEW_SEARCH_PARAM,
  ViewEnum,
} from '../../explore/explore.enums';
import {
  CRITICAL_PEOPLE_SEARCH_PARAM,
  DrawerTitle,
  FeedbackList,
  MATCHING_ENGINE_PERSON_DRAWER_ID,
  matchingEngineApi,
  MatchingEngineContributorDetailDrawer,
  NoMatchFound,
  PEOPLE_SEARCH_PARAM,
  UNVERIFIED_SKILL_SEARCH_PARAM,
} from '../matching-engine.utils';
import { ActiveFiltersContext } from './ActiveFiltersContext';
import { useGetAllPastFeedbacks } from './DiaryFeedbackView';
import { PeopleComparsionGrid } from './MatchingEngineComponents/PeopleComparisonGrid';
import { SkillFilterGrid } from './MatchingEngineComponents/SkillFilterGrid';
import { PeopleGrid } from './PeopleGrid';
import { PAGE_SIZE } from 'features/profile-data-grid/profile-data-grid.constants';

const MATCHING_ENGINE_CONTRIBUTOR_DETAILS_DRAWER_TABS = [
  {
    value: MatchingEngineContributorDetailDrawer.CurrentAllocation,
    label: 'Current Allocation',
  },
  { value: MatchingEngineContributorDetailDrawer.TrackRecord, label: 'Track Record' },
  { value: MatchingEngineContributorDetailDrawer.Feedback, label: 'Feedback' },
];

export type GetMatchingEngineSkillsRequestParams = SearchParams &
  SearchFilters & {
    contentType: ExploreTabEnum;
    cursor: string;
    size: number;
  };

export const getMatchingEngineSkills = (params: GetMatchingEngineSkillsRequestParams) => {
  return matchingEngineApi.get('skills', { params });
};

export const useMatchingEngineResults = () => {
  const { account: user, hasOrganization } = useSession();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('query') ?? '';

  const { sort, ...filters } = mapSearchParamsObject(searchParams, true) as {
    [prop: string]: any;
  };
  const sortArray = [sort];

  const getRecords = (params: any) => {
    const queryParams = createCursorQueryParams(params.pageParam, {
      term: query,
      ...filters,
      sort: sortArray,
      size: PAGE_SIZE,
    });

    return matchingEngineApi.get('search', { params: queryParams });
  };

  const queryKey = ['matching-engine', user?.organizationId, query, filters, sortArray];

  const queryResponse = useInfiniteQuery(queryKey, getRecords, {
    getNextPageParam: getNextCursorParam,
    entityKey: 'matches',
    staleTime: 10000,
    retry: 3, // llms can and will faill...so try harder!
  });

  const data = queryResponse.isSuccess ? queryResponse.data.pages : [];
  const total = queryResponse.data?.pageParams?.total || 0;

  return {
    total,
    ...queryResponse,
    data,
  };
};

const PreCard: React.FC = (obj) => {
  return (
    <Card noElevation>
      <pre>{JSON.stringify(obj, null, 2)}</pre>
    </Card>
  );
};

const useCurrentAllocations = (profileId: string, disabled?: boolean) => {
  const { account: user, hasOrganization } = useSession();

  const getRecords = () => {
    const params = {
      profileId,
    };
    return matchingEngineApi.get(`current-allocations`, { params });
  };

  const queryKey = [
    'matching-engine',
    user?.organizationId,
    profileId,
    'current-allocations',
  ];

  const queryResponse = useQuery(queryKey, getRecords, {
    enabled: !!(hasOrganization && profileId) && !disabled,
  });

  return {
    ...queryResponse,
    data: queryResponse.data?.data,
  };
};

const CurrentAllocations: React.FC<{
  profileId: string;
}> = ({ profileId }) => {
  const { palette } = useTheme();
  const { setNode, intersectionObserverEntry } = useIntersectionObserver();
  const { data, isLoading } = useCurrentAllocations(
    profileId,
    !intersectionObserverEntry?.isIntersecting,
  );
  if (isLoading || !data) {
    return (
      <Skeleton
        ref={setNode}
        variant="rectangular"
        sx={{ borderRadius: '0.75rem', mr: 2, mb: 3 }}
        height="6rem"
      />
    );
  }

  if (data.items?.length === 0) {
    return (
      <Typography variant="body4" color={palette.Text.Headline} sx={{ fontStyle: 'italic' }}>
        Allocation details not available.
      </Typography>
    );
  }
  return (
    <Typography variant="body3" color={palette.Text.Headline}>
      {(data?.items ?? []).map((item: any, index: number) =>
        item.type === 'WORK_ITEM' ? (
          <Box key={item.id} sx={{ mb: 2 }}>
            <RecentlyWorkedItem {...item} />
          </Box>
        ) : null,
      )}
    </Typography>
  );
};

// PersonLinkContainer rip-off -- main differences:
// - allow profileHeadline's tooltip to be different from profileHeadline (this way we can
//   inject more information in the tooltip)
// - (in future) on click, we should open a side drawer, rather than navigating to someone's
//   profile.

interface HeadlineData {
  itemsCount: number;
  highCriticalityCount: number;
  singleItemDetails?: {
    name: string;
    criticality: string;
    sponsor?: string;
  };
}

const getProfileHeadline = ({
  itemsCount,
  highCriticalityCount,
}: {
  itemsCount: number;
  highCriticalityCount: number;
}) => {
  if (!itemsCount || itemsCount === 0) {
    return 'Allocation details not available';
  }

  if (itemsCount === 1) {
    return `Working on 1 item`;
  }

  const criticalLabel =
    highCriticalityCount && highCriticalityCount > 0
      ? `(${highCriticalityCount} Critical)`
      : '';

  return `Working on ${itemsCount} items ${criticalLabel}`.trim();
};

const PersonSummary: React.FC<{ person: any }> = ({ person }) => {
  const { palette } = useTheme();
  return (
    <Stack sx={{ py: 0.5 }} spacing={1} direction="row" alignItems="center">
      <UserAvatar fullName={person?.fullName} src={person?.photo} size="medium" />
      <Stack minHeight="1.5rem" justifyContent="center">
        <Typography variant="body4" fontWeight="500" color={palette.LinkText.Default}>
          {person?.fullName}
        </Typography>
        <EllipsisText
          className="link-sub-title"
          lineclamp={1}
          variant="caption"
          color={palette.Extras.Extra1}
          title={
            person.allocationMetadata && (
              <Stack spacing={2}>
                <Typography variant="body4" sx={{ fontWeight: 600 }}>
                  Current allocations
                </Typography>
                <Box sx={{ whiteSpace: 'pre-line' }}>
                  {person.profileHeadlineExpanded ??
                    `${getProfileHeadline(person.allocationMetadata)}`}
                </Box>
              </Stack>
            )
          }
        >
          {person.allocationMetadata
            ? `${getProfileHeadline(person.allocationMetadata)}`
            : 'Allocation details not available'}
        </EllipsisText>
      </Stack>
    </Stack>
  );
};

export const MatchingEnginePersonDrawer: React.FC<{
  person: any;
  initialTab: MatchingEngineContributorDetailDrawer;
}> = ({ person, initialTab }) => {
  const { closeDrawer } = useDrawer();
  const fullName = person?.name;

  const [selectedTab, setSelectedTab] = useState(initialTab);

  const {
    data: { pastFeedbacks },
  } = useGetAllPastFeedbacks(person.profileID, {
    enabled: selectedTab === MatchingEngineContributorDetailDrawer.Feedback,
  });

  const closeCurrentDrawer = () => {
    closeDrawer(MATCHING_ENGINE_PERSON_DRAWER_ID);
  };

  return (
    <>
      <DrawerHeader>
        <Stack flexDirection="column" pl={2} pt={2} width="100%">
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
            <Stack flexDirection="row" alignItems="center">
              <CXAvatar src={person?.photo} alt={person?.name} />
              <DrawerTitle ml={2}>{fullName}</DrawerTitle>
            </Stack>

            <CloseButton clickHandler={closeCurrentDrawer} />
          </Stack>

          <ButtonTabs
            selectedValues={[selectedTab]}
            tabs={MATCHING_ENGINE_CONTRIBUTOR_DETAILS_DRAWER_TABS}
            onChange={setSelectedTab}
            scroll
            sx={{
              px: { xs: 1.5, md: 0 },
              pt: 2,
              gap: 0.75,
            }}
          />
        </Stack>
      </DrawerHeader>

      <Box
        sx={{
          height: 'calc(100% - 72px)',
          overflowY: 'auto',
          px: 3,
          mt: 2,
        }}
      >
        {(() => {
          switch (selectedTab) {
            case MatchingEngineContributorDetailDrawer.CurrentAllocation:
              return <CurrentAllocations profileId={person.profileID} />;
            case MatchingEngineContributorDetailDrawer.TrackRecord:
              return (
                <EndorsedSkillTrackRecord
                  profileId={person.profileID}
                  skillId={person.skillID}
                  stepId={person.stepID}
                />
              );
            case MatchingEngineContributorDetailDrawer.Feedback:
              return <FeedbackList feedbacks={pastFeedbacks} />;
            default:
              return null;
          }
        })()}
      </Box>
    </>
  );
};

const PersonResult: React.FC<{
  nmatches: number;
  setSelectedRows: React.Dispatch<React.SetStateAction<GridRowId[]>>;
  id: string;
  fullName: string;
  skills?: any[];
  verifiedSkills?: any[];
  index: number;
  total: number;
}> = ({ nmatches, setSelectedRows, skills, index, total, ...person }) => {
  const theme = useTheme();
  const borderColor = theme.palette.Buttons.Label.LabelDisabled;

  const isFirst = index === 0;
  const isLast = index === total - 1;

  return (
    <div
      style={{
        margin: 0,
        padding: 0,
        border: `1px solid ${borderColor}`,
        borderTopLeftRadius: isFirst ? 12 : 0,
        borderTopRightRadius: isFirst ? 12 : 0,
        borderBottomLeftRadius: isLast ? 12 : 0,
        borderBottomRightRadius: isLast ? 12 : 0,
      }}
    >
      <Box my={0.5}>
        <RecommendedPerson
          npeople={nmatches}
          skills={skills}
          setSelectedRows={setSelectedRows}
          {...person}
        />
      </Box>
    </div>
  );
};

const RecommendationScore: React.FC<{
  confirmedSkills: any[];
  maybeSkills: any[];
  missingSkills: any[];
}> = ({ confirmedSkills, maybeSkills, missingSkills }) => {
  return (
    <Tooltip
      title={
        <Stack spacing={2}>
          {confirmedSkills.length > 0 && (
            <>
              <Typography key="matching" variant="body4" sx={{ fontWeight: 600 }}>
                Verified:
              </Typography>
              {confirmedSkills.map((v) => (
                <Typography key={v.id} variant="body4">
                  {v.name}
                </Typography>
              ))}
            </>
          )}
          {maybeSkills.length > 0 && (
            <>
              <Typography key="matching" variant="body4" sx={{ fontWeight: 600 }}>
                Maybe:
              </Typography>
              {maybeSkills.map((v) => (
                <Typography key={v.id} variant="body4">
                  {v.name}
                </Typography>
              ))}
            </>
          )}
          {missingSkills.length > 0 && (
            <>
              <Typography key="matching" variant="body4" sx={{ fontWeight: 600 }}>
                Missing:
              </Typography>
              {missingSkills.map((v) => (
                <Typography key={v.id} variant="body4">
                  {v.name}
                </Typography>
              ))}
            </>
          )}
        </Stack>
      }
    >
      <Typography
        display="inline"
        variant="body4"
        sx={{ borderBottom: 'dotted', borderBottomWidth: 'thin' }}
      >
        <Stack direction="row" justifyContent="space-evenly">
          <Box display="inline">{confirmedSkills.length} Verified</Box>
          <Box display="inline" px={1}>
            •
          </Box>
          <Box display="inline">{maybeSkills.length} Maybe</Box>
          <Box display="inline" px={1}>
            •
          </Box>
          <Box display="inline">{missingSkills.length} Missing</Box>
        </Stack>
      </Typography>
    </Tooltip>
  );
};

const RecommendedPerson: React.FC<{
  npeople: number;
  setSelectedRows: React.Dispatch<React.SetStateAction<GridRowId[]>>;
  id: string;
  fullName: string;
  skills?: any[];
  verifiedSkills?: any[];
}> = ({ npeople, setSelectedRows, skills, verifiedSkills, ...person }) => {
  const activeFilters = useContext<ActiveFilters>(ActiveFiltersContext);
  const searchedSkills = activeFilters['matchingEngineSkill'] ?? [];
  const [overlappingSkills, missingSkills] = partition(
    searchedSkills,
    (v) => skills && skills.find((v2) => v2.id === v.id),
  );
  const [confirmedSkills, maybeSkills] = partition(
    overlappingSkills,
    (v) => verifiedSkills && verifiedSkills.find((v2) => v2.id === v.id),
  );

  const [selectedTab, setSelectedTab] = useState(
    MatchingEngineContributorDetailDrawer.CurrentAllocation,
  );

  const {
    data: { pastFeedbacks },
  } = useGetAllPastFeedbacks(person.id, {
    enabled: selectedTab === MatchingEngineContributorDetailDrawer.Feedback,
  });

  return (
    <Accordion
      sx={{
        boxShadow: 'unset',
        width: 1,
        background: 'inherit',
        // Without this, the last person link, when expanded, would shift down
        // a little bit, due to some extra margine getting automatically added
        '&.Mui-expanded': { mt: 0 },
        // Remove the top border: https://stackoverflow.com/a/76685872
        '&:before': { height: '0px' },
      }}
      // The content of Accordions is mounted by default even if the accordion is not expanded.
      // By enabling the unmountOnExit, we prevent the default behaviour.
      TransitionProps={{ unmountOnExit: true }}
      defaultExpanded={npeople === 1}
    >
      <StyledAccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          pr: 1,
          pl: 1,
          flexDirection: 'row',
          '& .MuiAccordionSummary-expandIconWrapper': {
            transform: 'rotate(90deg)',
            transition: 'transform 0.2s ease',
            '.Mui-expanded &': {
              transform: 'rotate(270deg)',
            },
          },
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
          },
        }}
      >
        <Stack direction="row" sx={{ width: 1, alignItems: 'flex-start' }}>
          <Box sx={{ pt: 0.25, pr: 1 }}>
            {searchedSkills.length > 0 && (
              <Checkbox
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRows((prev) => {
                    if (prev.includes(person.id)) {
                      return prev.filter((id) => id !== person.id);
                    } else {
                      return [...prev, person.id];
                    }
                  });
                }}
                onFocus={(e) => e.stopPropagation()}
              />
            )}
          </Box>

          <Stack direction="row" alignItems="center" sx={{ flexGrow: 1 }}>
            <PersonSummary person={person} />

            {searchedSkills.length > 0 && (
              <Stack
                direction="row"
                sx={{ flex: '0 0 auto', ml: 'auto', mr: 2, alignItems: 'center' }}
              >
                <RecommendationScore
                  confirmedSkills={confirmedSkills}
                  maybeSkills={maybeSkills}
                  missingSkills={missingSkills}
                />
              </Stack>
            )}
          </Stack>
        </Stack>
      </StyledAccordionSummary>

      <AccordionDetails sx={{ py: 0, ml: 1 }}>
        <Box>
          <ButtonTabs
            selectedValues={[selectedTab]}
            tabs={MATCHING_ENGINE_CONTRIBUTOR_DETAILS_DRAWER_TABS}
            onChange={setSelectedTab}
            scroll
            sx={{
              pt: 2,
              px: { xs: 1.5, md: 0 },
              gap: 0.75,
            }}
          />

          <Box sx={{ pt: 2, pb: 2, px: 1 }}>
            {(() => {
              switch (selectedTab) {
                case MatchingEngineContributorDetailDrawer.CurrentAllocation:
                  return <CurrentAllocations profileId={person.id} />;
                case MatchingEngineContributorDetailDrawer.TrackRecord:
                  return <EndorsedSkillTrackRecord profileId={person.id} />;
                case MatchingEngineContributorDetailDrawer.Feedback:
                  return <FeedbackList feedbacks={pastFeedbacks} />;
                default:
                  return null;
              }
            })()}
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const MatchingEngineCurrentAllocationToggle = () => {
  const { palette } = useTheme();
  const { isMobileView } = useView();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = [
    {
      value: 'any',
      label: 'Any',
      ...(isMobileView && { sx: { width: '100%' } }),
    },
    {
      value: 'non-critical',
      label: 'Not working on critical projects',
      ...(isMobileView && { sx: { width: '100%' } }),
    },
  ];
  const [filterType, setFilterType] = useState(
    !searchParams.get(CRITICAL_PEOPLE_SEARCH_PARAM) ? tabs[0].value : tabs[1].value,
  );

  const onTabChange = (value: string) => {
    setFilterType(value);
    const searchParams2 = new URLSearchParams(searchParams);
    if (value === 'any') {
      searchParams2.delete(CRITICAL_PEOPLE_SEARCH_PARAM);
    } else {
      searchParams2.set(CRITICAL_PEOPLE_SEARCH_PARAM, 'true');
    }
    setSearchParams(searchParams2);
  };

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="caption" color={palette.Text.Caption} mr={1}>
        Current allocation:
      </Typography>
      <ButtonTabs
        selectedValues={[filterType]}
        tabs={tabs}
        onChange={onTabChange}
        sx={{
          gap: 0.75,
        }}
      />
    </Stack>
  );
};

const MatchingEngineSkillFilterToggle = () => {
  const { palette } = useTheme();
  const { isMobileView } = useView();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = [
    {
      value: 'all',
      label: 'All',
      ...(isMobileView && { sx: { width: '100%' } }),
    },
    {
      value: 'confirmed',
      label: 'Manager verified skills only',
      ...(isMobileView && { sx: { width: '100%' } }),
    },
  ];
  const [filterType, setFilterType] = useState(
    !searchParams.get(UNVERIFIED_SKILL_SEARCH_PARAM) ? tabs[0].value : tabs[1].value,
  );

  const onTabChange = (value: string) => {
    setFilterType(value);
    const searchParams2 = new URLSearchParams(searchParams);
    if (value === 'all') {
      searchParams2.delete(UNVERIFIED_SKILL_SEARCH_PARAM);
    } else {
      searchParams2.set(UNVERIFIED_SKILL_SEARCH_PARAM, 'true');
    }
    setSearchParams(searchParams2);
  };

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="caption" color={palette.Text.Caption} mr={1}>
        Skill match:
      </Typography>
      <ButtonTabs
        selectedValues={[filterType]}
        tabs={tabs}
        onChange={onTabChange}
        sx={{
          gap: 0.75,
        }}
      />
    </Stack>
  );
};

type ResultsContainerProps = {
  data: any[];
  setSelectedRows: React.Dispatch<React.SetStateAction<GridRowId[]>>;
};

const ResultsContainer: React.FC<ResultsContainerProps> = ({
  data: matches,
  setSelectedRows,
}) => {
  const { isMobileView } = useView();
  const { emit: emitFiltersUpdated } = useEventEmitter<ActiveFilters>('FiltersUpdated');
  const activeFilters = useContext<ActiveFilters>(ActiveFiltersContext);

  useEffect(() => {
    const filterMetadata2 = matches.find((v) => v.type === 'FILTER_METADATA');
    if (filterMetadata2) {
      const { type: _, ...updatedFilters } = filterMetadata2;
      const activeFilters2 = { ...activeFilters };
      let changed = false;
      for (const [akey, ukey] of [
        ['matchingEngineSkill', 'MATCHING_ENGINE_SKILL'],
        ['country', 'COUNTRY'],
        ['city', 'CITY'],
        ['workLocation', 'WORK_LOCATION'],
        ['department', 'DEPARTMENT'],
        ['division', 'DIVISION'],
      ]) {
        const a = (activeFilters[akey] ?? [])
          .slice()
          .map((v) => v.id)
          .sort()
          .join(',');
        const b = (updatedFilters[ukey] ?? [])
          .slice()
          .map((v: any) => v.id)
          .sort()
          .join(',');
        if (a !== b) {
          changed = true;
          if (!updatedFilters[ukey]) {
            delete activeFilters2[akey];
          } else {
            activeFilters2[akey] = updatedFilters[ukey];
          }
        }
      }
      if (changed) {
        emitFiltersUpdated(activeFilters2);
      }
    }
  }, [matches, activeFilters, emitFiltersUpdated]);

  return (
    <>
      <Stack
        justifyContent="space-between"
        mx={1}
        mt={-1}
        pb={1}
        spacing={isMobileView ? 0.75 : 0}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <MatchingEngineCurrentAllocationToggle />
          {activeFilters.matchingEngineSkill?.length > 0 && (
            <MatchingEngineSkillFilterToggle />
          )}
        </Stack>
      </Stack>
      <ResultsView matches={matches} setSelectedRows={setSelectedRows} />
    </>
  );
};

type ResultViewProps = {
  matches: any[];
  setSelectedRows: React.Dispatch<React.SetStateAction<GridRowId[]>>;
};

const useContributorProfiles = () => {
  const [searchParams] = useSearchParams();
  const rawPeopleParam = searchParams.get(PEOPLE_SEARCH_PARAM);
  const profileIDs = rawPeopleParam?.split('%') ?? [];

  const filters = mapSearchParamsObject(searchParams, true, [
    'viewType',
    PEOPLE_SEARCH_PARAM,
  ]) as { [prop: string]: any };

  const finalOutput = useQuery(
    ['matching-profiles', profileIDs],
    () =>
      matchingEngineApi
        .get('matching-profiles', { params: { ...filters, profileIDs } })
        .then((res: any) => res.data),
    { enabled: !!rawPeopleParam },
  );

  return finalOutput;
};

const ResultsView: React.FC<ResultViewProps> = ({ matches, setSelectedRows }) => {
  const { '*': viewType } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFilters = useContext<ActiveFilters>(ActiveFiltersContext);

  const { data: peopleForComparisonGrid, isLoading, isError } = useContributorProfiles();

  const peopleMatches = matches.filter((e) => e.type === 'PERSON');
  const skills = activeFilters['matchingEngineSkill'] ?? [];
  const currentViewType = searchParams.get(VIEW_SEARCH_PARAM);

  if (currentViewType === COMPARISON_VIEW_PARAM) {
    if (isLoading) return <SearchSkeletonView />;
    if (isError || !peopleForComparisonGrid) return <NoMatchFound />;

    return <PeopleComparsionGrid skills={skills} people={peopleForComparisonGrid.matches} />;
  }
  // If not comparison view, clear past history of selection.
  setSelectedRows([]);
  if (!peopleMatches.length) {
    return <NoMatchFound />;
  }

  if (viewType === ViewEnum.TABLE) {
    if (skills?.length > 0) {
      return (
        <SkillFilterGrid
          people={peopleMatches}
          skills={skills}
          setSelectedRows={setSelectedRows}
        />
      );
    } else {
      return <PeopleGrid people={peopleMatches} />;
    }
  }

  return (
    <>
      {matches.map((match) => {
        switch (match.type) {
          case 'FILTER_METADATA':
            return null;
          case 'PERSON': {
            const personIndex = peopleMatches.findIndex((p) => p.id === match.id);
            return (
              <PersonResult
                key={match.id}
                index={personIndex}
                total={peopleMatches.length}
                nmatches={matches.length}
                setSelectedRows={setSelectedRows}
                {...match}
              />
            );
          }
          default:
            return <PreCard key={match.id} {...match} />;
        }
      })}
    </>
  );
};

type MatchingEngineSearchViewProps = {
  setSelectedRows: React.Dispatch<React.SetStateAction<GridRowId[]>>;
};

const MatchingEngineSearchView: React.FC<MatchingEngineSearchViewProps> = ({
  setSelectedRows,
}) => {
  return (
    <InfiniteScrollContainer
      key={ExploreTabEnum.MATCHING_ENGINE}
      infiniteApiHook={useMatchingEngineResults}
      InfiniteListContainer={(props) => (
        <ResultsContainer {...props} setSelectedRows={setSelectedRows} />
      )}
      LoaderComponent={SearchSkeletonView}
      showFooter={false}
    />
  );
};

export default React.memo(MatchingEngineSearchView);
